import {
  AfterViewInit,
  Component,
  HostListener,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LayoutService } from '@app/shared/components/layout/layout.service';
import { PaginatorComponent } from '@app/shared/components/paginator/paginator.component';
import { SearchWidgetComponent } from '@app/shared/components/search-widget/search-widget.component';
import { ResponsiveTableComponent } from '@app/shared/components/table/responsive-table.component';
import { DialogData, Tab, ToastService } from '@teorema/patron';
import { firstValueFrom } from 'rxjs';
import { AccessType, AccessTypeProfile, BwUser } from 'src/app/models/bw-user.model';
import { Company } from 'src/app/models/company.model';
import { Customer } from 'src/app/models/customer.model';
import { FilterField } from 'src/app/models/filter-field.model';
import { FilterOperator, FindOptions, OrderBy } from 'src/app/models/find-options.model';
import { Item } from 'src/app/models/item.model';
import { PaymentCondition } from 'src/app/models/payment-condition.model';
import { PriceTable } from 'src/app/models/price-table.model';
import { StockLocale } from 'src/app/models/stock-locale.model';
import { UserDepartment } from 'src/app/models/user-department.model';
import { User } from 'src/app/models/user.model';
import {
  BwUserService,
  PanelConfigurationService,
  UserDepartmentService,
  UserService,
  utils,
} from 'src/app/shared';
import { EntityPickerSearchParams } from 'src/app/features/v3ndor/components/entity-picker/entity-picker.types';
import { SearchService } from 'src/app/shared/services/search.service';
import { WebLicenseCustomerService } from 'src/app/shared/services/web-license-customer.service';
import { WebLicense } from 'src/app/models/web-license.model';
import { BwUserLicensesComponent } from 'src/app/modules/manager/company-group-licenses/bw-user-licenses.component';
import { AuthService } from '../../auth.service';

@Component({
  templateUrl: './bw-user-view.component.html',
  styleUrls: ['./bw-user-view.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BwUserViewComponent implements AfterViewInit {
  patronUtils = utils;

  id?: string;
  bwUser?: BwUser;
  user?: User;
  loadingUser: boolean = false;
  savingBwUser: boolean = false;
  savingUser: boolean = false;
  basicForm!: FormGroup;
  specificForm!: FormGroup;
  licensesForm!: FormGroup;
  reportsChartsForm!: FormGroup;
  discountLimitEditIndex?: number;
  salesGoalsForm: FormGroup;
  newDiscountLimit = {
    minValue: null,
    maxValue: null,
    discount: null,
  };
  tabs: Tab[] = [];
  selectedTab?: string;

  profilesFindOptions: FindOptions = {
    orderBy: 'name',
  };
  profilesAddItemProperties = [
    {
      key: 'color',
      value: utils.getAccessTypeColor(AccessType.User),
      key_compare: 'type',
      value_compare: AccessTypeProfile.User,
    },
    {
      key: 'color',
      value: utils.getAccessTypeColor(AccessType.Customer),
      key_compare: 'type',
      value_compare: AccessTypeProfile.Customer,
    },
    {
      key: 'color',
      value: utils.getAccessTypeColor(AccessType.Supplier),
      key_compare: 'type',
      value_compare: AccessTypeProfile.Supplier,
    },
  ];

  searchItemsFindOptions: FindOptions = {
    orderBy: 'description',
    fields: [
      { valueField: '_description', label: 'Item', highlight: true },
      { valueField: 'gtin', label: 'Gtin', highlight: true },
    ],
    filters: [
      new FilterField({
        label: 'Família',
        name: 'family_id',
        bindValue: 'id',
        bindLabel: '_name',
        orderBy: 'ref',
        multiple: true,
        type: 'select',
        modelName: 'families',
      }),
      new FilterField({
        label: 'Grupo',
        name: 'group_id',
        bindValue: 'id',
        bindLabel: '_name',
        orderBy: 'ref',
        multiple: true,
        type: 'select',
        modelName: 'groups',
      }),
      new FilterField({
        label: 'SubGrupo',
        name: 'sup_group_id',
        bindValue: 'id',
        bindLabel: '_name',
        orderBy: 'ref',
        multiple: true,
        type: 'select',
        modelName: 'sub-groups',
      }),
      new FilterField({
        label: 'Marca',
        name: 'brand_id',
        bindValue: 'id',
        bindLabel: '_name',
        orderBy: 'ref',
        multiple: true,
        type: 'select',
        modelName: 'brands',
      }),
      new FilterField({
        label: 'Tipo',
        name: 'item_type_id',
        bindValue: 'id',
        bindLabel: '_name',
        orderBy: 'ref',
        multiple: true,
        type: 'select',
        modelName: 'item-types',
      }),
      new FilterField({
        label: 'SubTipo',
        name: 'item_sub_type_id',
        bindValue: 'id',
        bindLabel: '_name',
        orderBy: 'ref',
        multiple: true,
        type: 'select',
        modelName: 'item-sub-types',
      }),
      new FilterField({
        label: 'Controle',
        name: 'item_control_id',
        bindValue: 'id',
        bindLabel: '_name',
        orderBy: 'ref',
        multiple: true,
        type: 'select',
        modelName: 'item-controls',
      }),
    ],
  };

  searchStockLocalesFindOptions: FindOptions = {
    orderBy: 'ref',
    fields: [
      { valueField: '_name', label: 'Local de estoque', highlight: true },
      { valueField: 'type', label: 'Tipo', columnClass: 'text-center', columnWidth: 60 },
    ],
  };

  searchPriceTablesFindOptions: FindOptions = {
    orderBy: 'ref',
    fields: [{ valueField: '_description', label: 'Tabela', highlight: true }],
  };

  searchPaymentConditionsFindOptions: FindOptions = {
    orderBy: 'ref',
    fields: [{ valueField: '_name', label: 'Condição de pagamento', highlight: true }],
    filters: [
      new FilterField({
        label: 'Ativo',
        name: 'active',
        value: true,
        visible: false,
      }),
    ],
  };

  customersFindOptions: FindOptions = {
    reset: 1,
    perPage: 20,
    orderBy: 'persons.name',
    storageKey: 'user-customers-find-options',
    withs: [],
    q: '',
    fields: [
      {
        label: 'Ref',
        sortField: 'persons.ref',
      },
      {
        label: 'Nome',
        sortField: 'persons.name',
      },
      {
        label: 'Ativo',
        sortField: 'persons.active',
        columnWidth: 110,
        columnClass: 'text-center',
      },
      {
        label: 'Mobile',
        sortField: 'persons.go_to_mobile',
        columnWidth: 110,
        columnClass: 'text-center',
      },
    ],
    filters: [
      new FilterField({ name: 'active', key: 'persons.active', label: 'Ativo', type: 'boolean' }),
      new FilterField({
        name: 'go_to_mobile',
        key: 'persons.go_to_mobile',
        label: 'Mobile',
        type: 'boolean',
      }),
    ],
  };

  panelConfig$ = this.panelConfigService.panelConfig$;

  configDefault = { discountLimit: [], itemExclude: false, itemFilter: false };

  @ViewChild('itemsTable') itemsTable?: ResponsiveTableComponent;
  @ViewChild('stockLocalesTable') stockLocalesTable?: ResponsiveTableComponent;
  @ViewChild('priceTablesTable') priceTablesTable?: ResponsiveTableComponent;
  @ViewChild('paymentConditionsTable') paymentConditionsTable?: ResponsiveTableComponent;
  @ViewChild('customersTable') customersTable?: ResponsiveTableComponent;

  @ViewChild('basicTab') basicTab!: TemplateRef<any>;
  @ViewChild('sellersTab') sellersTab!: TemplateRef<any>;
  @ViewChild('customersTab') customersTab!: TemplateRef<any>;
  @ViewChild('itemsTab') itemsTab!: TemplateRef<any>;
  @ViewChild('stockLocalesTab') stockLocalesTab!: TemplateRef<any>;
  @ViewChild('priceTablesTab') priceTablesTab!: TemplateRef<any>;
  @ViewChild('paymentConditionsTab') paymentConditionsTab!: TemplateRef<any>;
  @ViewChild('fiscalOperationsTab') fiscalOperationsTab!: TemplateRef<any>;
  @ViewChild('companiesTab') companiesTab!: TemplateRef<any>;
  @ViewChild('specificTab') specificTab!: TemplateRef<any>;
  @ViewChild('licensesTab') licensesTab!: TemplateRef<any>;
  @ViewChild('systemLicensesTab') systemLicensesTab!: TemplateRef<any>;
  @ViewChild('templatesTab') templatesTab!: TemplateRef<any>;
  @ViewChild('metasTab') metasTab!: TemplateRef<any>;
  @ViewChild('departmentsTab') departmentsTab!: TemplateRef<any>;
  @ViewChild('pipelinesTab') pipelinesTab!: TemplateRef<any>;
  @ViewChild(BwUserLicensesComponent) bwUserLicensesComponent?: BwUserLicensesComponent;

  readonly searchSellersFn = (params: EntityPickerSearchParams) => {
    return this.searchService.handleSearch('sellers', {
      q: params.query || undefined,
      page: params.page,
      perPage: params.perPage,
    }).then((r: any) => ({
      items: r.data ?? [],
      total: r.meta?.total ?? r.total ?? 0,
    }));
  };

  constructor(
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private bwUserService: BwUserService,
    private userDepartmentService: UserDepartmentService,
    private toastService: ToastService,
    private formBuilder: FormBuilder,
    private layoutService: LayoutService,
    private router: Router,
    private panelConfigService: PanelConfigurationService,
    private searchService: SearchService,
    private licenseService: WebLicenseCustomerService,
    private authService: AuthService
  ) {
    this.id = this.activatedRoute.snapshot.params['id'];
    this.basicForm = this.formBuilder.group({
      name: [{ value: '', disabled: true }],
      ref: [{ value: '', disabled: true }],
      login: [{ value: '', disabled: true }],
      email: [{ value: '', disabled: true }],
      last_access: [{ value: '', disabled: true }],
      bw_user_profiles: [null],
      show_prices: [null],
    });

    this.specificForm = formBuilder.group({
      read_all_customers: [null],
      emit_nfe: [null],
      order_counterpassword: [null],
      can_use_campaign: [null],
      itemFilter: [null],
      itemExclude: [{ value: null, disabled: true }],
    });

    this.reportsChartsForm = formBuilder.group({
      reports_charts: [null],
    });

    this.licensesForm = formBuilder.group({
      licenses_web: [null],
      licenses_mobile: [null],
    });

    this.salesGoalsForm = formBuilder.group({
      daily_sales_goals: [null],
      monthly_sales_goals: [null],
    });
  }

  async ngAfterViewInit() {
    this.tabs = [{ header: 'Dados básicos', template: this.basicTab, index: 0 }];

    if (this.id) {
      await this.findBwUser();

      // Chamado #0000577675 — revalida `control_licenses` aqui (não em polling): é o carregamento
      // da aba onde o valor é usado, então o custo de rede só existe quando a tela é aberta/revisitada.
      await this.authService.refreshCurrentCompanyGroup();
      if (this.isLicenseControlActive) {
        this.loadLicenseState();
      }

      if (this.bwUser?.is_user) {
        await this.findUser();
        this.tabs.push(
          { header: 'Vendedores', template: this.sellersTab, index: 1 },
          { header: 'Clientes', template: this.customersTab, index: 2 },
          { header: 'Itens', template: this.itemsTab, index: 3 },
          { header: 'Locais de estoque', template: this.stockLocalesTab, index: 4 },
          { header: 'Tabelas de preço', template: this.priceTablesTab, index: 5 },
          { header: 'Condições de pagamento', template: this.paymentConditionsTab, index: 6 },
          { header: 'Operações', template: this.fiscalOperationsTab, index: 7 },
          { header: 'Empresas', template: this.companiesTab, index: 8 },
          { header: 'Configurações específicas', template: this.specificTab, index: 9 },
          { header: 'Licenças', template: this.licensesTab, index: 10 },
          { header: 'Templates', template: this.templatesTab, index: 11 },
          { header: 'Metas', template: this.metasTab, index: 12 },
          { header: 'Departamentos', template: this.departmentsTab, index: 13 }
        );

        // Chamado #0000579137: acesso às pipelines de IA por usuário (soma ao que os perfis dão).
        // O módulo "Pipelines de IA" libera ver a aba; o servidor confere ao salvar.
        if (utils.checkAccess(['AiPipelines'], true).result) {
          this.tabs.push({ header: 'Pipelines', template: this.pipelinesTab, index: 15 });
        }

        // Chamado 0000577675 (tarefa 16): licença nomeada por sistema. A aba "Licenças" acima é o
        // controle antigo por plataforma (web/mobile), que continua valendo.
        if (utils.checkAccess(['Licenses'], true).result) {
          this.tabs.push({
            header: 'Licenças de sistema',
            template: this.systemLicensesTab,
            index: 14,
          });
        }
      }
    }
  }

  async findBwUser() {
    this.loadingUser = true;
    if (!this.id) return;
    await this.bwUserService
      .findById(this.id)
      .then(response => {
        this.bwUser = response;
        this.updateBwUserForm();
      })
      .catch(err => {
        if (!err?.messageShown)
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao carregar os dados do usuário',
          });
      })
      .finally(() => {
        this.loadingUser = false;
      });
  }

  // --- Chamado #0000577675 — vincular assento de licença direto na aba "Dados básicos" -------

  /** Controle ativo: sem assento, o usuário fica sem acesso (mesmo aviso de "Novo usuário"). */
  get isLicenseControlActive(): boolean {
    return utils.getCompanyGroup()?.control_licenses === true;
  }

  get canLinkLicense(): boolean {
    return this.isLicenseControlActive && this.licenseService.canWrite();
  }

  availableLicenses: WebLicense[] = [];
  /** Visão simplificada: no máximo 1 assento "atual"; a aba "Licenças" lida com múltiplos sistemas. */
  currentLicense: WebLicense | null = null;
  loadingLicenses = false;
  selectedLicenseId: string | null = null;
  linkingLicense = false;

  /** Revalida ao voltar o foco: reflete mudanças feitas em outra tela/pessoa sem timer em segundo plano. */
  @HostListener('window:focus')
  async onWindowFocusRefreshLicenseState() {
    if (!this.bwUser?.id) return;
    await this.authService.refreshCurrentCompanyGroup();
    if (this.isLicenseControlActive) {
      await this.loadLicenseState();
    }
  }

  async loadLicenseState() {
    if (!this.bwUser?.id) return;

    this.loadingLicenses = true;
    try {
      const response = await this.licenseService.list({ bw_user_id: this.bwUser.id, size: 10 });
      this.currentLicense = response?.data?.[0] ?? null;
      this.selectedLicenseId = this.currentLicense?.id ?? null;

      if (this.currentLicense) {
        this.availableLicenses = [];
      } else {
        const available = await this.licenseService.list({ only_available: true });
        this.availableLicenses = available?.data ?? [];
      }
    } catch (error) {
      this.currentLicense = null;
      this.availableLicenses = [];
      this.toastService.show({
        color: 'error',
        message: 'Houve um erro ao carregar os assentos de licença',
      });
    } finally {
      this.loadingLicenses = false;
    }
  }

  async linkLicense() {
    if (!this.selectedLicenseId || !this.bwUser?.id || this.linkingLicense) return;

    this.linkingLicense = true;
    try {
      await this.licenseService.link(this.selectedLicenseId, this.bwUser.id);
      this.toastService.show({ color: 'success', message: 'Assento de licença vinculado' });
      await this.loadLicenseState();
      // Aba "Licenças" pode já estar montada (patron-tabview mantém as abas visitadas): sem isto
      // ela só refletiria o vínculo depois de um F5 manual.
      await this.bwUserLicensesComponent?.load();
    } catch (error: any) {
      this.toastService.show({
        color: 'error',
        message:
          error?.error?.errors?.bw_user_id?.[0] ??
          error?.error?.message ??
          'Não foi possível vincular o assento de licença',
      });
    } finally {
      this.linkingLicense = false;
    }
  }

  async unlinkLicense() {
    if (!this.currentLicense || this.linkingLicense) return;

    this.linkingLicense = true;
    try {
      await this.licenseService.unlink(this.currentLicense.id);
      this.toastService.show({ color: 'success', message: 'Assento de licença desvinculado' });
      await this.loadLicenseState();
      await this.bwUserLicensesComponent?.load();
    } catch (error: any) {
      this.toastService.show({
        color: 'error',
        message: error?.error?.message ?? 'Não foi possível desvincular o assento de licença',
      });
    } finally {
      this.linkingLicense = false;
    }
  }

  updateBwUserForm() {
    if (!this.bwUser) return;
    let { name, login, email, bw_user_profiles, config, is_customer, is_user, is_supplier } =
      this.bwUser;

    bw_user_profiles = utils.getProfilesThisCompanyGroup(bw_user_profiles);

    const filter = new FilterField({
      label: 'Tipo',
      name: 'type',
      type: 'text',
      multiple: true,
      operator: FilterOperator.in,
      value: [],
    });
    if (is_user) {
      filter.value.push('U');
    }
    if (is_customer) {
      filter.value.push('C');
    }
    if (is_supplier) {
      filter.value.push('S');
    }
    if (filter.value.length) {
      this.profilesFindOptions.filters = [filter];
    }

    this.basicForm.patchValue({
      name,
      login,
      email,
      bw_user_profiles,
      last_access: config?.last_access !== undefined ? new Date(config?.last_access) : 'nunca',
      show_prices: config?.show_prices,
    });
  }

  async findUser() {
    this.loadingUser = true;
    if (!this.id) return;
    await this.userService
      .findById(this.id)
      .then(response => {
        if (response?.data) {
          this.user = response.data;

          if (this.user?.config) {
            this.user!!.config!!.itemFilter = utils.toBoolean(response.data.config.itemFilter);
            this.user!!.config!!.itemExclude = utils.toBoolean(response.data.config.itemExclude);
          }

          this.searchItemsFindOptions.queryParams =
            this.searchPaymentConditionsFindOptions.queryParams =
            this.searchPriceTablesFindOptions.queryParams =
            this.searchStockLocalesFindOptions.queryParams =
              { user_id: this.user?.id };

          this.updateUserForm();

          // Carregar departamentos do usuário
          this.getUserDepartments();
        }
      })
      .catch(err => {
        if (!err?.messageShown)
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao carregar os dados do usuário',
          });
      })
      .finally(() => {
        this.loadingUser = false;
      });
  }

  totalCustomers: number = 0;
  customers: Customer[] = [];
  loadingCustomers: boolean = false;

  // Departamentos
  userDepartments: UserDepartment[] = [];
  loadingUserDepartments: boolean = false;
  searchDepartmentsFindOptions: FindOptions = {
    orderBy: 'name',
    fields: [{ valueField: '_name', label: 'Departamento', highlight: true }],
    filters: [new FilterField({ label: 'Ativo', name: 'active', value: true, visible: false })],
  };

  @ViewChild('customersSearchWidget') customersSearchWidget?: SearchWidgetComponent;
  @ViewChild('customersPaginator') customersPaginator?: PaginatorComponent;
  async getCustomers() {
    if (!this.user?.id) return;
    this.loadingCustomers = true;
    let response = await this.userService
      .getCustomers(this.user.id, this.customersFindOptions)
      .catch(err => {
        if (!err?.messageShown)
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao carregar os clientes',
          });
      });

    if (response?.data) {
      this.customers = response.data;
    }

    if (response?.meta?.total != null && response?.meta?.total >= 0) {
      this.totalCustomers = response.meta.total;
    }
    this.loadingCustomers = false;
  }

  async paginateCustomers(findOptions?: FindOptions) {
    if (findOptions) this.customersFindOptions = { ...this.customersFindOptions, ...findOptions };

    if (this.customersFindOptions?.q) {
      this.customersSearchWidget?.updateValue(this.customersFindOptions.q);
    }

    await this.getCustomers();
  }

  onSearchCustomer(searchText?: string) {
    if (searchText != this.customersFindOptions.q) {
      this.customersFindOptions.q = searchText;
      this.goToFirstPageCustomers();
    }
  }

  goToFirstPageCustomers() {
    this.paginateCustomers({ page: 1 });
    if (this.customersPaginator) this.customersPaginator.component.first = 0;
  }

  // Métodos para gerenciar departamentos
  async getUserDepartments() {
    if (!this.user?.id) return;
    this.loadingUserDepartments = true;
    try {
      const response = await firstValueFrom(
        this.userDepartmentService.getUserDepartments(this.user.id)
      );
      this.userDepartments = response || [];
    } catch (err: any) {
      if (!err?.messageShown)
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao carregar os departamentos do usuário',
        });
    } finally {
      this.loadingUserDepartments = false;
    }
  }

  async addUserToDepartment(departmentId: string, manager?: boolean) {
    if (!this.user?.id) return;
    this.loadingUser = true;
    try {
      await firstValueFrom(
        this.userDepartmentService.addUserToDepartment(this.user.id, departmentId, manager)
      );
      this.toastService.show({
        color: 'success',
        message: 'Usuário adicionado ao departamento com sucesso!',
      });
      this.getUserDepartments();
    } catch (err: any) {
      if (!err?.messageShown)
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao adicionar usuário ao departamento',
        });
    } finally {
      this.loadingUser = false;
    }
  }

  async removeUserFromDepartment(departmentId: string) {
    if (!this.user?.id) return;
    this.loadingUser = true;
    try {
      await firstValueFrom(
        this.userDepartmentService.removeUserFromDepartment(this.user.id, departmentId)
      );
      this.toastService.show({
        color: 'success',
        message: 'Usuário removido do departamento com sucesso!',
      });
      this.getUserDepartments();
    } catch (err: any) {
      if (!err?.messageShown)
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao remover usuário do departamento',
        });
    } finally {
      this.loadingUser = false;
    }
  }

  async updateManagerStatus(departmentId: string, manager: boolean) {
    if (!this.user?.id) return;
    this.loadingUser = true;
    try {
      await firstValueFrom(
        this.userDepartmentService.updateManagerStatus(this.user.id, departmentId, manager)
      );
      this.toastService.show({
        color: 'success',
        message: 'Status de gerente atualizado com sucesso!',
      });
      this.getUserDepartments();
    } catch (err: any) {
      if (!err?.messageShown)
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao atualizar status de gerente',
        });
    } finally {
      this.loadingUser = false;
    }
  }

  applyOrder(orderBy: OrderBy[]) {
    this.customersFindOptions.orderBy = orderBy;
    this.paginateCustomers();
    this.customersTable?.updateSortMeta(this.customersFindOptions);
  }

  totalItems: number = 0;
  items: Item[] = [];
  loadingItems: boolean = false;
  itemsFindOptions: FindOptions = {};
  @ViewChild('itemsSearchWidget') itemsSearchWidget?: SearchWidgetComponent;
  @ViewChild('itemsPaginator') itemsPaginator?: PaginatorComponent;
  async getItems() {
    if (!this.user?.id) return;
    this.loadingItems = true;
    let response = await this.userService
      .getItems(this.user.id, this.itemsFindOptions)
      .catch(err => {
        if (!err?.messageShown)
          this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os itens' });
      });

    if (response?.data) {
      this.items = response.data;
    }

    if (response?.meta?.total != null && response?.meta?.total >= 0) {
      this.totalItems = response.meta.total;
    }
    this.loadingItems = false;
  }

  async paginateItems(findOptions?: FindOptions) {
    if (findOptions) this.itemsFindOptions = { ...this.itemsFindOptions, ...findOptions };

    if (this.itemsFindOptions?.q) {
      this.itemsSearchWidget?.updateValue(this.itemsFindOptions.q);
    }

    await this.getItems();
  }

  onSearchItem(searchText?: string) {
    if (searchText != this.itemsFindOptions.q) {
      this.itemsFindOptions.q = searchText;
      this.goToFirstPageItems();
    }
  }

  goToFirstPageItems() {
    this.paginateItems({ page: 1 });
    if (this.itemsPaginator) this.itemsPaginator.component.first = 0;
  }

  updateUserForm() {
    if (!this.user) return;
    let {
      ref,
      read_all_customers,
      emit_nfe,
      order_counterpassword,
      can_use_campaign,
      config,
      licenses_mobile,
      licenses_web,
      reports_charts,
      daily_sales_goals,
      monthly_sales_goals,
    } = this.user;
    if (ref) {
      this.basicForm.patchValue({ ref });
    }
    this.specificForm.patchValue({
      read_all_customers,
      emit_nfe,
      order_counterpassword,
      can_use_campaign,
      itemFilter: config?.itemFilter,
      itemExclude: config?.itemExclude,
    });
    if (config?.itemFilter) {
      this.specificForm.get('itemExclude')?.enable();
    }
    this.licensesForm.patchValue({
      licenses_web: licenses_web,
      licenses_mobile: licenses_mobile,
    });

    this.salesGoalsForm.patchValue({
      daily_sales_goals: daily_sales_goals,
      monthly_sales_goals: monthly_sales_goals,
    });

    if (reports_charts != null) {
      this.reportsChartsForm.patchValue({
        reports_charts: JSON.parse(reports_charts ?? ''),
      });
    }
  }

  async saveBwUser() {
    if (this.savingBwUser) return;

    let { bw_user_profiles, show_prices } = this.basicForm.value;
    bw_user_profiles = bw_user_profiles?.concat(
      utils.getProfilesDifferCompanyGroup(this.bwUser?.bw_user_profiles)
    );
    let body: any = { bw_user_profiles };

    let config = this.bwUser?.config;
    if (show_prices != null) {
      config = { ...config, show_prices };
      let company_group_id = utils.getCompanyGroup().id;
      body = { ...body, config, company_group_id };
    }

    let result = await this.bwUserService.patch(this.bwUser!.id, body).catch(err => {
      if (!err?.messageShown)
        this.toastService.show({ color: 'error', message: 'Houve um erro ao salvar o usuário' });
    });

    this.savingBwUser = false;

    if (result) {
      this.toastService.show({ color: 'success', message: 'Salvo com sucesso!' });

      result = {
        ...result,
        is_customer: this.bwUser?.is_customer,
        is_supplier: this.bwUser?.is_supplier,
        is_user: this.bwUser?.is_user,
      };
      this.bwUser = result;
      this.updateBwUserForm();

      if (this.bwUser.id == utils.getUser().id) {
        this.layoutService.callUpdateNavItems.emit();
        this.router.navigate(['/']);
      }
    }
  }

  itemFilterChange(event: any) {
    const field = this.specificForm?.get('itemExclude');
    field?.disable();
    if (event.checked) field?.enable();
  }

  async saveUser() {
    if (this.savingUser) return;

    let {
      read_all_customers,
      emit_nfe,
      order_counterpassword,
      can_use_campaign,
      itemExclude,
      itemFilter,
    } = this.specificForm.value;
    let { licenses_web, licenses_mobile } = this.licensesForm.value;

    let { reports_charts } = this.reportsChartsForm.value;
    // `?? []` para o campo vazio gravar "[]" em vez da string "null".
    reports_charts = JSON.stringify(reports_charts ?? []);

    let { daily_sales_goals, monthly_sales_goals } = this.salesGoalsForm.value;

    let body: any = {
      read_all_customers,
      emit_nfe,
      order_counterpassword,
      can_use_campaign,
      licenses_web,
      licenses_mobile,
      reports_charts,
      daily_sales_goals,
      monthly_sales_goals,
    };
    let config = this.user?.config || this.configDefault;
    if (itemExclude != null) {
      config.itemExclude = itemExclude;
    }
    if (itemFilter != null) {
      config.itemFilter = itemFilter;
      if (!itemFilter) {
        config.itemExclude = false;
      }
    }

    body.config = config;

    this.discountLimitEditIndex = undefined;
    if (!this.user) return;
    let result = await this.userService.patch(this.user.id, body).catch(err => {
      if (!err?.messageShown)
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao salvar a configuração',
        });
    });

    if (result) {
      this.toastService.show({ color: 'success', message: 'Salvo com sucesso!' });
      this.user = result;
      this.updateUserForm();
    }
  }

  addDiscountLimit() {
    if (!this.user) return;
    if (!this.user.config) this.user.config = this.configDefault;
    if (!this.user.config.discountLimit) this.user.config.discountLimit = [];

    if (!this.newDiscountLimit.discount) {
      this.toastService.show({ color: 'info', message: 'Informe a porcentagem de desconto' });
      return;
    }

    this.user.config.discountLimit.push(JSON.parse(JSON.stringify(this.newDiscountLimit)));
    this.saveUser();
    this.newDiscountLimit = {
      minValue: null,
      maxValue: null,
      discount: null,
    };
  }

  cancelEditDiscountLimit() {
    if (!this.user) return;
    if (!this.user.config) this.user.config = this.configDefault;
    if (!this.user.config.discountLimit) return;
    if (this.discountLimitEditIndex == undefined) return;
    let oldConfig = JSON.parse(JSON.stringify(this.user.config));

    this.user.config.discountLimit[this.discountLimitEditIndex] =
      oldConfig.discountLimit[this.discountLimitEditIndex];
    this.discountLimitEditIndex = undefined;
  }

  removeDiscountLimit(index: number) {
    if (!this.user) return;
    if (!this.user.config) this.user.config = this.configDefault;
    if (!this.user.config.discountLimit) return;
    if (confirm('Tem certeza que deseja excluir essa regra?')) {
      this.user?.config?.discountLimit.splice(index, 1);
      this.saveUser();
    }
  }

  removeConfirm: DialogData = {
    buttonIcon: 'bi-trash3',
    buttonColor: 'danger',
  };

  removeItemConfirm: DialogData = {
    ...this.removeConfirm,
    buttonSize: 'sm',
  };

  openRemoveItemConfirm(item: any) {
    this.removeItemConfirm = {
      ...this.removeItemConfirm,
      message: `Tem certeza que deseja remover o item ${item._description}?`,
    };
    if (this.itemsTable) this.itemsTable.blockSelection = true;
    this.removeItemConfirm.acceptHandler = () => {
      this.removeRelation([item.id], 'items');
    };
  }

  removeCheckedItemsConfirm: DialogData = {
    ...this.removeConfirm,
    label: 'Remover marcados',
  };

  openRemoveCheckedItemsConfirm(ids: string[]) {
    this.removeCheckedItemsConfirm = {
      ...this.removeCheckedItemsConfirm,
      message: `Tem certeza que deseja remover ${ids.length > 1 ? 'os ' + ids.length + ' itens marcados' : 'o item marcado'}?`,
    };
    this.removeCheckedItemsConfirm.acceptHandler = () => {
      this.removeRelation(ids, 'items');
    };
  }

  onHideConfirmItemRemove() {
    setTimeout(() => {
      if (this.itemsTable) this.itemsTable.blockSelection = false;
    }, 10);
  }

  removeStockLocaleConfirm: DialogData = {
    ...this.removeItemConfirm,
  };

  openRemoveStockLocaleConfirm(stockLocale: any) {
    this.removeStockLocaleConfirm = {
      ...this.removeStockLocaleConfirm,
      message: `Tem certeza que deseja remover o local de estoque ${stockLocale._name}?`,
    };
    if (this.stockLocalesTable) this.stockLocalesTable.blockSelection = true;
    this.removeStockLocaleConfirm.acceptHandler = () => {
      this.removeRelation([stockLocale.id], 'stock_locales');
    };
  }

  onHideConfirmStockLocaleRemove() {
    setTimeout(() => {
      if (this.stockLocalesTable) this.stockLocalesTable.blockSelection = false;
    }, 10);
  }

  removePriceTableConfirm: DialogData = {
    ...this.removeItemConfirm,
  };

  openRemovePriceTableConfirm(priceTable: any) {
    this.removePriceTableConfirm = {
      ...this.removePriceTableConfirm,
      message: `Tem certeza que deseja remover a tabela de preço ${priceTable._description}?`,
    };
    if (this.priceTablesTable) this.priceTablesTable.blockSelection = true;
    this.removePriceTableConfirm.acceptHandler = () => {
      this.removeRelation([priceTable.id], 'price_tables');
    };
  }

  onHideConfirmPriceTableRemove() {
    setTimeout(() => {
      if (this.priceTablesTable) this.priceTablesTable.blockSelection = false;
    }, 10);
  }

  removePaymentConditionConfirm: DialogData = {
    ...this.removeItemConfirm,
  };

  removeDepartmentConfirm: DialogData = {
    ...this.removeItemConfirm,
  };

  openRemovePaymentConditionConfirm(paymentCondition: any) {
    this.removePaymentConditionConfirm = {
      ...this.removePaymentConditionConfirm,
      message: `Tem certeza que deseja remover a condição de pagamento ${paymentCondition._name}?`,
    };
    if (this.paymentConditionsTable) this.paymentConditionsTable.blockSelection = true;
    this.removePaymentConditionConfirm.acceptHandler = () => {
      this.removeRelation([paymentCondition.id], 'payment_conditions');
    };
  }

  onHideConfirmPaymentConditionRemove() {
    setTimeout(() => {
      if (this.paymentConditionsTable) this.paymentConditionsTable.blockSelection = false;
    }, 10);
  }

  openRemoveDepartmentConfirm(userDepartment: any) {
    this.removeDepartmentConfirm = {
      ...this.removeDepartmentConfirm,
      message: `Tem certeza que deseja remover o usuário do departamento ${userDepartment.department?.name || userDepartment.department_id}?`,
    };
    this.removeDepartmentConfirm.acceptHandler = () => {
      this.removeUserFromDepartment(userDepartment.department_id);
    };
  }

  get isSyncActive(): boolean {
    return utils.isActiveSync();
  }

  get currentLinkedSeller(): any {
    const s = this.user?.sellers?.[0];
    if (!s) return null;
    return { ...s, _name: `${s.ref} - ${s.name}` };
  }

  async onSellerPickerChange(seller: any): Promise<void> {
    if (!this.user) return;
    if (seller === null || seller === undefined) {
      const currentSellers = this.user.sellers ?? [];
      if (currentSellers.length > 0) {
        await this.removeRelation([currentSellers[0].id], 'sellers');
      }
    } else {
      await this.insertRelation([seller.id], 'sellers');
    }
  }

  async insertRelation(ids: string[], type: string) {
    this.loadingUser = true;
    if (!this.user) return;
    const response = await this.userService.insertRelation(this.user.id, ids, type).catch(err => {
      if (!err?.messageShown)
        this.toastService.show({
          color: 'error',
          message: `Houve um erro ao adicionar ${type} ao usuário`,
        });
    });

    if (response?.data) {
      this.user = response.data;

      if (type == 'items') this.goToFirstPageItems();
    }

    this.loadingUser = false;
  }

  async removeRelation(ids: string[], type: string) {
    this.loadingUser = true;
    if (!this.user) return;
    const response = await this.userService.removeRelation(this.user.id, ids, type).catch(err => {
      if (!err.messageShown)
        this.toastService.show({
          color: 'error',
          message: `Houve um erro ao remover ${type} do usuário`,
        });
    });

    if (response?.data) {
      this.user = response.data;

      if (type == 'items') this.paginateItems();
    }

    if (this.itemsTable?.checkedItems) this.itemsTable.checkedItems = [];

    this.loadingUser = false;
  }

  async setCountingStockLocale(stockLocale: StockLocale) {
    this.loadingUser = true;
    if (!this.user) return;
    const response = await this.userService
      .setCountingStockLocale(this.user.id, stockLocale)
      .catch(err => {
        stockLocale.is_counting = !stockLocale.is_counting ? 1 : 0;
        if (!err?.messageShown)
          this.toastService.show({
            color: 'error',
            message: `Houve um erro ao ${stockLocale.is_counting ? 'ativar' : 'desativar'} a contagem de itens`,
          });
      });

    if (response?.data) this.user = response.data;

    this.loadingUser = false;
  }

  async setMainStockLocale(stockLocale: StockLocale) {
    this.loadingUser = true;
    if (!this.user) return;
    const response = await this.userService
      .setMainStockLocale(this.user.id, stockLocale)
      .catch(err => {
        stockLocale.main = !stockLocale.main ? 1 : 0;
        if (!err?.messageShown)
          this.toastService.show({
            color: 'error',
            message: `Houve um erro ao alterar o local de estoque padrão`,
          });
      });

    if (response?.data) this.user = response.data;

    this.loadingUser = false;
  }

  async setMainPriceTable(priceTable: PriceTable) {
    this.loadingUser = true;
    if (!this.user) return;
    const response = await this.userService
      .setMainPriceTable(this.user.id, priceTable.id)
      .catch(err => {
        priceTable.main = !priceTable.main ? 1 : 0;
        if (!err?.messageShown)
          this.toastService.show({
            color: 'error',
            message: `Houve um erro ao alterar a tabela de preço padrão`,
          });
      });

    if (response?.data) this.user = response.data;

    this.loadingUser = false;
  }

  async setMainPaymentCondition(paymentCondition: PaymentCondition) {
    this.loadingUser = true;
    if (!this.user) return;
    const response = await this.userService
      .setMainPaymentCondition(this.user.id, paymentCondition.id)
      .catch(err => {
        paymentCondition.main = !paymentCondition.main ? 1 : 0;
        if (!err?.messageShown)
          this.toastService.show({
            color: 'error',
            message: `Houve um erro ao alterar a condição de pagamento padrão`,
          });
      });

    if (response?.data) this.user = response.data;

    this.loadingUser = false;
  }

  async setMainCompany(company: Company) {
    this.loadingUser = true;
    if (!this.user) return;
    const response = await this.userService.setMainCompany(this.user.id, company.id).catch(err => {
      company.main = !company.main ? 1 : 0;
      if (!err?.messageShown)
        this.toastService.show({
          color: 'error',
          message: `Houve um erro ao alterar a empresa padrão`,
        });
    });

    if (response?.data) this.user = response.data;

    this.loadingUser = false;
  }

  cloneModalVisible: boolean = false;
  cloneLoading: boolean = false;
  cloneConfirmModalVisible: boolean = false;
  cloneConfirmMessage: string = '';
  cloneConfirmWarningMessage: string = '';
  cloneForm?: FormGroup;
  cloneOptions: {
    label: string;
    value: string;
  }[] = [
    { label: 'Itens', value: 'items' },
    { label: 'Condições', value: 'payment_conditions' },
    { label: 'Tabelas de preço', value: 'price_tables' },
    { label: 'Locais de estoque', value: 'stock_locales' },
  ];
  openCloneModal() {
    this.cloneForm = this.formBuilder.group({
      attributes: [null, Validators.required],
      userClone: [null, Validators.required],
    });

    this.cloneModalVisible = false;
    setTimeout(() => {
      this.cloneModalVisible = true;
    });
  }

  openCloneUserConfirm() {
    if (!this.cloneForm) return;
    if (this.cloneForm.invalid) {
      Object.keys(this.cloneForm.controls).forEach(key => {
        this.cloneForm?.controls[key].markAsDirty();
      });

      this.toastService.show({
        color: 'error',
        message: 'Parece que há um ou mais campos inválidos no formulário',
      });
      return;
    }
    const { attributes, userClone } = this.cloneForm.value;

    if (!attributes?.length || !userClone) return;
    let labels = '';
    for (let i = 0; i < attributes.length; i++) {
      const atr = attributes[i];
      if (i > 0) {
        labels += i == attributes.length - 1 ? ' e ' : ', ';
      }
      labels += atr.label;
    }

    const currentUserName = `${this.user?.ref || 'Sem ref'} - ${this.user?.name || this.user?.login || this.user?.email || 'Sem nome'}`;
    this.cloneConfirmMessage = `Tem certeza que deseja clonar ${labels || 'os dados'} do usuário ${userClone._name} para o usuário ${currentUserName}?`;
    this.cloneConfirmWarningMessage = `${labels || 'Os dados'} do usuário ${currentUserName} serão substituídos(as)!`;

    this.cloneModalVisible = false;
    this.cloneConfirmModalVisible = false;
    setTimeout(() => {
      this.cloneConfirmModalVisible = true;
    });
  }

  cancelClone() {
    this.cloneConfirmModalVisible = false;
    this.cloneModalVisible = false;
    setTimeout(() => {
      this.cloneModalVisible = true;
    });
  }

  async cloneUserData() {
    if (!this.cloneForm) return;
    if (!this.user) return;

    const { attributes, userClone } = this.cloneForm.value;
    const atrValues = attributes.map((atr: any) => atr.value);
    this.cloneLoading = true;
    const response: any = await this.userService
      .cloneUserData(this.user.id, userClone.id, atrValues)
      .catch(err => {
        if (!err?.messageShown)
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao clonar dados do usuário',
          });
      })
      .finally(() => {
        this.cloneLoading = false;
      });

    if (response?.data) {
      const data: any = {};
      atrValues.forEach((atr: string) => {
        if (atr == 'items') {
          this.getItems();
        } else {
          data[atr] = response.data[atr];
        }
      });
      this.user = { ...this.user, ...data };

      this.toastService.show({ color: 'success', message: 'Os dados foram clonados!' });
      this.cloneConfirmModalVisible = false;
    }
  }

  resetPasswordModalVisible: boolean = false;
  openResetPasswordModal() {
    this.resetPasswordModalVisible = false;
    setTimeout(() => {
      this.resetPasswordModalVisible = true;
    });
  }

  resetPasswordLoading: boolean = false;
  async generateNewRandomPassword() {
    if (!this.bwUser?.id) return;
    this.resetPasswordLoading = true;
    await this.bwUserService
      .generateNewRandomPassword(this.bwUser.id)
      .then((response: any) => {
        if (response?.data) {
          this.bwUser = { ...this.bwUser, ...response.data };
          this.toastService.show({ color: 'success', message: 'A nova senha foi gerada' });
        }
      })
      .catch(() => {
        this.toastService.show({ color: 'error', message: 'Houve um erro ao atualizar a senha' });
      })
      .finally(() => {
        this.resetPasswordLoading = false;
      });
  }

  managementPasswordModalVisible: boolean = false;
  managementPasswordLoading: boolean = false;

  shouldShowManagementPasswordButton(): boolean {
    if (!this.bwUser?.is_user) {
      return false;
    }

    if (utils.isSupport()) {
      return true;
    }

    const companyGroup = utils.getCompanyGroup();
    const hasDevReadWrite = utils.checkAccess(['Development'], true, true).result;

    if (companyGroup?.alias === 'teorema' && hasDevReadWrite) {
      return true;
    }

    return false;
  }

  openManagementPasswordModal() {
    this.managementPasswordModalVisible = false;
    setTimeout(() => {
      this.managementPasswordModalVisible = true;
    });
  }

  async reinstallUserPasswordFromGestao() {
    if (!this.bwUser?.email) {
      this.toastService.show({ color: 'error', message: 'Usuário sem email para atualização.' });
      return;
    }

    const companyGroup = utils.getCompanyGroup();
    if (!companyGroup?.alias) {
      this.toastService.show({
        color: 'error',
        message: 'Company Group não encontrado no contexto.',
      });
      return;
    }

    this.managementPasswordLoading = true;
    await this.bwUserService
      .reinstallUserPassword(companyGroup.alias, this.bwUser.email)
      .then(() => {
        this.toastService.show({ color: 'success', message: 'Senha atualizada com sucesso.' });
        this.managementPasswordModalVisible = false;
      })
      .catch(() => {
        this.toastService.show({ color: 'error', message: 'Falha ao atualizar a senha.' });
      })
      .finally(() => {
        this.managementPasswordLoading = false;
      });
  }
}
